// 棚卸し（stocktake）— Stocky parity v2 Step 2。Starter 以上。
// 反映は delta ではなく**絶対値セット**＝カウント中に売れた分を二重計上しない。
// expected は開始時点のスナップショットで、以後どの操作でも変わらない。
import { apiFetch } from "./client";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";
import type { StockyStocktakeListMetadata } from "./stocky_csv_import";

export interface StocktakeItem {
  shopify_variant_id: StockyPublicBigIntId;
  sku: string | null;
  title: string | null;
  current_product_title?: string | null;
  current_variant_title?: string | null;
  /** 開始時点のシステム在庫。以後不変。不明なら null（捏造しない）。 */
  expected_qty: number | null;
  /** 実数カウント。null は「未カウント」＝適用の対象外。 */
  counted_qty: number | null;
  /** counted - expected。どちらか不明なら null。 */
  variance: number | null;
  before_qty: number | null;
  after_qty: number | null;
  /** Stocky export のラベル付き原値。通常棚卸しは null。 */
  source_current_stock?: number | null;
  source_adjustment_qty?: number | null;
}

export type StocktakeStatus = "counting" | "applied" | "cancelled";

export type StocktakeAppliedTargetEvidenceStatus =
  | "available"
  | "unavailable"
  | "not_applicable";

/** Exact source-to-destination receipt captured before Shopify was called. */
export interface StocktakeAppliedTarget {
  stocktake_item_id: StockyPublicBigIntId;
  source_shopify_variant_id: StockyPublicBigIntId;
  source_sku: string | null;
  source_title: string | null;
  counted_qty: number;
  target_shopify_product_id: StockyPublicBigIntId;
  target_shopify_variant_id: StockyPublicBigIntId;
  target_shopify_inventory_item_id: StockyPublicBigIntId;
  target_shopify_location_id: StockyPublicBigIntId;
  target_sku: string | null;
  target_product_title: string;
  target_variant_title: string | null;
  confirmed_as_same_current_product: boolean;
}

export interface StocktakeAppliedTargetEvidence {
  status: StocktakeAppliedTargetEvidenceStatus;
  targets: StocktakeAppliedTarget[];
}

export interface Stocktake {
  id: StockyPublicBigIntId;
  location_id: StockyPublicBigIntId | null;
  location_name: string | null;
  status: StocktakeStatus;
  note: string | null;
  started_at: string;
  applied_at: string | null;
  /** DB内部の applied とは別。Stocky側の在庫適用済みはCSVからは不明。 */
  imported_from_stocky?: boolean;
  source_file_sha256?: string | null;
  source_file_name?: string | null;
  source_import_id?: string | null;
  source_quantity_interpretation?: SourceQuantityInterpretation | null;
  stocky_stocktake_list?: StockyStocktakeListMetadata | null;
  source_counted_on?: string | null;
  source_location_id?: StockyPublicBigIntId | null;
  source_shopify_location_id?: StockyPublicBigIntId | null;
  source_location_name?: string | null;
  source_location_active?: boolean | null;
  source_metadata_revision?: number;
  source_metadata_history?: SourceMetadataRevision[];
  applied_target_evidence: StocktakeAppliedTargetEvidence;
  items: StocktakeItem[];
  /**
   * 上限（1,000件）で載らなかった商品数。作成応答でのみ非0。
   * 🔴 非0のカウント表は「全商品」ではない＝画面が警告を出す。旧APIでは欠ける。
   */
  truncated_candidates?: number;
  /** 作成応答のみ。全対象内の0-based開始位置。 */
  candidate_offset?: number;
  /** 作成応答のみ。終売品を除く棚卸し対象の全件数。 */
  candidate_total?: number | null;
}

export interface StocktakeCandidates {
  total: number;
  batch_size: number;
}

export type StocktakeApplicationStatus =
  | "ready"
  | "confirmation_required"
  | "blocked"
  | "pending_recovery"
  | "reconciliation_required"
  | "finalizing";

export interface StocktakeApplicationTarget {
  stocktake_item_id: StockyPublicBigIntId;
  source_shopify_variant_id: StockyPublicBigIntId;
  source_sku: string | null;
  source_title: string | null;
  counted_qty: number;
  target_shopify_product_id: StockyPublicBigIntId;
  target_shopify_variant_id: StockyPublicBigIntId;
  target_sku: string | null;
  target_product_title: string;
  target_variant_title: string | null;
  uses_confirmed_current_product: boolean;
}

export interface StocktakeApplicationBlocker {
  stocktake_item_id: StockyPublicBigIntId;
  source_shopify_variant_id: StockyPublicBigIntId;
  source_sku: string | null;
  source_title: string | null;
  counted_qty: number;
  reason_code: string;
}

export interface StocktakeApplicationPreview {
  stocktake_id: StockyPublicBigIntId;
  status: StocktakeApplicationStatus;
  confirmation_token: string | null;
  targets: StocktakeApplicationTarget[];
  blockers: StocktakeApplicationBlocker[];
  completed_chunks: number;
  total_chunks: number;
  completed_items: number;
  total_items: number;
}

export interface StocktakeSummary {
  id: StockyPublicBigIntId;
  location_id: StockyPublicBigIntId | null;
  location_name: string | null;
  status: StocktakeStatus;
  note: string | null;
  started_at: string;
  applied_at: string | null;
  imported_from_stocky?: boolean;
  source_file_sha256?: string | null;
  source_file_name?: string | null;
  source_import_id?: string | null;
  source_quantity_interpretation?: SourceQuantityInterpretation | null;
  stocky_stocktake_list?: StockyStocktakeListMetadata | null;
  source_counted_on?: string | null;
  source_location_id?: StockyPublicBigIntId | null;
  source_shopify_location_id?: StockyPublicBigIntId | null;
  source_location_name?: string | null;
  source_location_active?: boolean | null;
  source_metadata_revision?: number;
  item_count: number;
  counted_count: number;
}

export type SourceQuantityInterpretation = "unknown" | "counted" | "expected";

export interface SourceMetadataRevision {
  changed_at: string;
  counted_on: string | null;
  location_name: string | null;
}

function canonicalNullableId(value: unknown, label: string): string | null {
  try {
    return value == null ? null : canonicalStockyPublicBigIntId(value);
  } catch {
    throw new TypeError(`${label} is invalid`);
  }
}

function normalizeStocktakeItem(value: unknown): StocktakeItem {
  if (value === null || typeof value !== "object") {
    throw new TypeError("stocktake item response is invalid");
  }
  const item = value as Omit<StocktakeItem, "shopify_variant_id"> & {
    shopify_variant_id: unknown;
  };
  return {
    ...item,
    shopify_variant_id: canonicalStockyPublicBigIntId(item.shopify_variant_id),
  };
}

const APPLIED_TARGET_KEYS = new Set([
  "stocktake_item_id",
  "source_shopify_variant_id",
  "source_sku",
  "source_title",
  "counted_qty",
  "target_shopify_product_id",
  "target_shopify_variant_id",
  "target_shopify_inventory_item_id",
  "target_shopify_location_id",
  "target_sku",
  "target_product_title",
  "target_variant_title",
  "confirmed_as_same_current_product",
]);

function hasExactKeys(
  record: Record<string, unknown>,
  expected: ReadonlySet<string>,
): boolean {
  const keys = Object.keys(record);
  return keys.length === expected.size && keys.every((key) => expected.has(key));
}

function normalizeAppliedTarget(value: unknown): StocktakeAppliedTarget {
  const target = applicationRecord(value, "stocktake applied target evidence");
  if (
    !hasExactKeys(target, APPLIED_TARGET_KEYS) ||
    typeof target.target_product_title !== "string" ||
    target.target_product_title.trim() === "" ||
    typeof target.confirmed_as_same_current_product !== "boolean"
  ) {
    throw new TypeError("stocktake applied target evidence response is invalid");
  }
  return {
    stocktake_item_id: canonicalStockyPublicBigIntId(target.stocktake_item_id),
    source_shopify_variant_id: canonicalStockyPublicBigIntId(
      target.source_shopify_variant_id,
    ),
    source_sku: applicationNullableString(target.source_sku, "source_sku"),
    source_title: applicationNullableString(
      target.source_title,
      "source_title",
    ),
    counted_qty: applicationCount(
      target.counted_qty,
      "stocktake applied target counted_qty",
      1_000_000_000,
    ),
    target_shopify_product_id: canonicalStockyPublicBigIntId(
      target.target_shopify_product_id,
    ),
    target_shopify_variant_id: canonicalStockyPublicBigIntId(
      target.target_shopify_variant_id,
    ),
    target_shopify_inventory_item_id: canonicalStockyPublicBigIntId(
      target.target_shopify_inventory_item_id,
    ),
    target_shopify_location_id: canonicalStockyPublicBigIntId(
      target.target_shopify_location_id,
    ),
    target_sku: applicationNullableString(target.target_sku, "target_sku"),
    target_product_title: target.target_product_title,
    target_variant_title: applicationNullableString(
      target.target_variant_title,
      "target_variant_title",
    ),
    confirmed_as_same_current_product:
      target.confirmed_as_same_current_product,
  };
}

function normalizeAppliedTargetEvidence(
  value: unknown,
): StocktakeAppliedTargetEvidence {
  const evidence = applicationRecord(value, "stocktake applied target evidence");
  if (
    !hasExactKeys(evidence, new Set(["status", "targets"])) ||
    (evidence.status !== "available" &&
      evidence.status !== "unavailable" &&
      evidence.status !== "not_applicable") ||
    !Array.isArray(evidence.targets)
  ) {
    throw new TypeError("stocktake applied target evidence response is invalid");
  }
  const targets = evidence.targets.map(normalizeAppliedTarget);
  if (
    (evidence.status === "available") !== (targets.length > 0) ||
    new Set(targets.map((target) => String(target.stocktake_item_id))).size !==
      targets.length ||
    new Set(targets.map((target) => String(target.target_shopify_variant_id)))
      .size !== targets.length ||
    new Set(
      targets.map((target) => String(target.target_shopify_inventory_item_id)),
    ).size !== targets.length ||
    new Set(targets.map((target) => String(target.source_shopify_variant_id)))
      .size !== targets.length ||
    targets.some(
      (target) =>
        !target.confirmed_as_same_current_product &&
        target.source_shopify_variant_id !==
          target.target_shopify_variant_id,
    ) ||
    (targets.length > 0 &&
      new Set(
        targets.map((target) => String(target.target_shopify_location_id)),
      ).size !== 1)
  ) {
    throw new TypeError("stocktake applied target evidence response is invalid");
  }
  return {
    status: evidence.status,
    targets,
  };
}

export function normalizeStocktake(value: unknown): Stocktake {
  if (value === null || typeof value !== "object") {
    throw new TypeError("stocktake response is invalid");
  }
  const stocktake = value as Omit<
    Stocktake,
    | "id"
    | "location_id"
    | "source_location_id"
    | "source_shopify_location_id"
    | "applied_target_evidence"
    | "items"
  > & {
    id: unknown;
    location_id: unknown;
    source_location_id?: unknown;
    source_shopify_location_id?: unknown;
    applied_target_evidence: unknown;
    items: unknown;
  };
  if (!Array.isArray(stocktake.items)) {
    throw new TypeError("stocktake items response is invalid");
  }
  const appliedTargetEvidence = normalizeAppliedTargetEvidence(
    stocktake.applied_target_evidence,
  );
  const imported = stocktake.imported_from_stocky === true;
  const evidenceIsApplicable = stocktake.status === "applied" && !imported;
  if (
    evidenceIsApplicable ===
    (appliedTargetEvidence.status === "not_applicable")
  ) {
    throw new TypeError("stocktake applied target evidence response is invalid");
  }
  const items = stocktake.items.map(normalizeStocktakeItem);
  if (appliedTargetEvidence.status === "available") {
    const countedItems = items.filter((item) => item.counted_qty !== null);
    const itemsBySourceVariant = new Map(
      countedItems.map((item) => [String(item.shopify_variant_id), item]),
    );
    if (
      itemsBySourceVariant.size !== countedItems.length ||
      countedItems.length !== appliedTargetEvidence.targets.length ||
      appliedTargetEvidence.targets.some((target) => {
        const item = itemsBySourceVariant.get(
          String(target.source_shopify_variant_id),
        );
        return (
          item === undefined ||
          item.sku !== target.source_sku ||
          item.title !== target.source_title ||
          item.counted_qty !== target.counted_qty ||
          item.after_qty !== target.counted_qty
        );
      })
    ) {
      throw new TypeError("stocktake applied target evidence response is invalid");
    }
  }
  return {
    ...stocktake,
    id: canonicalStockyPublicBigIntId(stocktake.id),
    location_id: canonicalNullableId(stocktake.location_id, "location_id"),
    source_location_id: canonicalNullableId(
      stocktake.source_location_id,
      "source_location_id",
    ),
    source_shopify_location_id: canonicalNullableId(
      stocktake.source_shopify_location_id,
      "source_shopify_location_id",
    ),
    applied_target_evidence: appliedTargetEvidence,
    items,
  };
}

function normalizeStocktakeSummary(value: unknown): StocktakeSummary {
  if (value === null || typeof value !== "object") {
    throw new TypeError("stocktake summary response is invalid");
  }
  const summary = value as Omit<
    StocktakeSummary,
    "id" | "location_id" | "source_location_id" | "source_shopify_location_id"
  > & {
    id: unknown;
    location_id: unknown;
    source_location_id?: unknown;
    source_shopify_location_id?: unknown;
  };
  return {
    ...summary,
    id: canonicalStockyPublicBigIntId(summary.id),
    location_id: canonicalNullableId(summary.location_id, "location_id"),
    source_location_id: canonicalNullableId(
      summary.source_location_id,
      "source_location_id",
    ),
    source_shopify_location_id: canonicalNullableId(
      summary.source_shopify_location_id,
      "source_shopify_location_id",
    ),
  };
}

const STOCKTAKE_APPLICATION_STATUSES = new Set<StocktakeApplicationStatus>([
  "ready",
  "confirmation_required",
  "blocked",
  "pending_recovery",
  "reconciliation_required",
  "finalizing",
]);

function applicationRecord(
  value: unknown,
  label: string,
): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${label} response is invalid`);
  }
  return value as Record<string, unknown>;
}

function applicationNullableString(
  value: unknown,
  label: string,
): string | null {
  if (value === null) return null;
  if (typeof value !== "string")
    throw new TypeError(`${label} response is invalid`);
  return value;
}

function applicationCount(
  value: unknown,
  label: string,
  maximum?: number,
): number {
  if (
    typeof value !== "number" ||
    !Number.isSafeInteger(value) ||
    value < 0 ||
    (maximum !== undefined && value > maximum)
  ) {
    throw new TypeError(`${label} response is invalid`);
  }
  return value;
}

function normalizeApplicationTarget(
  value: unknown,
): StocktakeApplicationTarget {
  const target = applicationRecord(value, "stocktake application target");
  const countedQty = applicationCount(
    target.counted_qty,
    "stocktake application target counted_qty",
    1_000_000_000,
  );
  if (
    typeof target.target_product_title !== "string" ||
    target.target_product_title.trim() === "" ||
    typeof target.uses_confirmed_current_product !== "boolean"
  ) {
    throw new TypeError("stocktake application target response is invalid");
  }
  return {
    stocktake_item_id: canonicalStockyPublicBigIntId(target.stocktake_item_id),
    source_shopify_variant_id: canonicalStockyPublicBigIntId(
      target.source_shopify_variant_id,
    ),
    source_sku: applicationNullableString(target.source_sku, "source_sku"),
    source_title: applicationNullableString(
      target.source_title,
      "source_title",
    ),
    counted_qty: countedQty,
    target_shopify_product_id: canonicalStockyPublicBigIntId(
      target.target_shopify_product_id,
    ),
    target_shopify_variant_id: canonicalStockyPublicBigIntId(
      target.target_shopify_variant_id,
    ),
    target_sku: applicationNullableString(target.target_sku, "target_sku"),
    target_product_title: target.target_product_title,
    target_variant_title: applicationNullableString(
      target.target_variant_title,
      "target_variant_title",
    ),
    uses_confirmed_current_product: target.uses_confirmed_current_product,
  };
}

function normalizeApplicationBlocker(
  value: unknown,
): StocktakeApplicationBlocker {
  const blocker = applicationRecord(value, "stocktake application blocker");
  if (
    typeof blocker.reason_code !== "string" ||
    blocker.reason_code.trim() === ""
  ) {
    throw new TypeError("stocktake application blocker response is invalid");
  }
  return {
    stocktake_item_id: canonicalStockyPublicBigIntId(blocker.stocktake_item_id),
    source_shopify_variant_id: canonicalStockyPublicBigIntId(
      blocker.source_shopify_variant_id,
    ),
    source_sku: applicationNullableString(blocker.source_sku, "source_sku"),
    source_title: applicationNullableString(
      blocker.source_title,
      "source_title",
    ),
    counted_qty: applicationCount(
      blocker.counted_qty,
      "stocktake application blocker counted_qty",
      1_000_000_000,
    ),
    reason_code: blocker.reason_code,
  };
}

export function normalizeStocktakeApplicationPreview(
  value: unknown,
): StocktakeApplicationPreview {
  const preview = applicationRecord(value, "stocktake application preview");
  if (
    typeof preview.status !== "string" ||
    !STOCKTAKE_APPLICATION_STATUSES.has(
      preview.status as StocktakeApplicationStatus,
    ) ||
    !Array.isArray(preview.targets) ||
    !Array.isArray(preview.blockers)
  ) {
    throw new TypeError("stocktake application preview response is invalid");
  }
  const status = preview.status as StocktakeApplicationStatus;
  const token = applicationNullableString(
    preview.confirmation_token,
    "confirmation_token",
  );
  if (
    (token !== null && !/^[0-9a-f]{64}$/.test(token)) ||
    (status === "confirmation_required" && token === null) ||
    (status !== "confirmation_required" && token !== null)
  ) {
    throw new TypeError(
      "stocktake application confirmation response is invalid",
    );
  }
  const targets = preview.targets.map(normalizeApplicationTarget);
  const blockers = preview.blockers.map(normalizeApplicationBlocker);
  const completedChunks = applicationCount(
    preview.completed_chunks,
    "completed_chunks",
    4,
  );
  const totalChunks = applicationCount(preview.total_chunks, "total_chunks", 4);
  const completedItems = applicationCount(
    preview.completed_items,
    "completed_items",
    1_000,
  );
  const totalItems = applicationCount(
    preview.total_items,
    "total_items",
    1_000,
  );
  if (
    completedChunks > totalChunks ||
    completedItems > totalItems ||
    (status === "blocked" && blockers.length === 0) ||
    (status !== "blocked" && blockers.length !== 0) ||
    (status === "confirmation_required" &&
      !targets.some((target) => target.uses_confirmed_current_product)) ||
    ((status === "ready" || status === "confirmation_required") &&
      (targets.length !== totalItems || completedItems !== 0))
  ) {
    throw new TypeError("stocktake application progress response is invalid");
  }
  return {
    stocktake_id: canonicalStockyPublicBigIntId(preview.stocktake_id),
    status,
    confirmation_token: token,
    targets,
    blockers,
    completed_chunks: completedChunks,
    total_chunks: totalChunks,
    completed_items: completedItems,
    total_items: totalItems,
  };
}

export const STOCKTAKE_STATUS_LABELS: Record<StocktakeStatus, string> = {
  counting: "カウント中",
  applied: "適用済み",
  cancelled: "中止",
};

export async function listStocktakes(): Promise<StocktakeSummary[]> {
  const data = await apiFetch<{ stocktakes: unknown[] }>("/api/stocktakes");
  return data.stocktakes.map(normalizeStocktakeSummary);
}

export async function getStocktake(
  id: StockyPublicBigIntId,
): Promise<Stocktake> {
  const result = await apiFetch<unknown>(
    `/api/stocktakes/${canonicalStockyPublicBigIntId(id)}`,
  );
  return normalizeStocktake(result);
}

export async function updateStocktakeSourceMetadata(
  id: StockyPublicBigIntId,
  input: {
    update_id: string;
    expected_revision: number;
    counted_on: string | null;
    location_id: StockyPublicBigIntId | null;
  },
): Promise<Stocktake> {
  const result = await apiFetch<unknown>(
    `/api/stocktakes/${canonicalStockyPublicBigIntId(id)}/source-metadata`,
    {
      method: "PATCH",
      body: {
        ...input,
        location_id:
          input.location_id == null
            ? null
            : canonicalStockyPublicBigIntId(input.location_id),
      },
    },
  );
  return normalizeStocktake(result);
}

export async function startStocktake(input: {
  note?: string;
  candidate_offset?: number;
}, idempotencyKey: string): Promise<Stocktake> {
  const result = await apiFetch<unknown>("/api/stocktakes", {
    method: "POST",
    body: input,
    headers: { "Idempotency-Key": idempotencyKey },
  });
  return normalizeStocktake(result);
}

export async function getStocktakeCandidateCount(): Promise<StocktakeCandidates> {
  return apiFetch<StocktakeCandidates>("/api/stocktakes/candidates/count");
}

export async function setStocktakeCounts(
  id: StockyPublicBigIntId,
  counts: { shopify_variant_id: StockyPublicBigIntId; counted_qty: number }[],
): Promise<Stocktake> {
  const result = await apiFetch<unknown>(
    `/api/stocktakes/${canonicalStockyPublicBigIntId(id)}/counts`,
    {
      method: "PATCH",
      body: {
        counts: counts.map((count) => ({
          ...count,
          shopify_variant_id: canonicalStockyPublicBigIntId(
            count.shopify_variant_id,
          ),
        })),
      },
    },
  );
  return normalizeStocktake(result);
}

export async function updateStocktakeNote(
  id: StockyPublicBigIntId,
  note: string | null,
): Promise<Stocktake> {
  const result = await apiFetch<unknown>(
    `/api/stocktakes/${canonicalStockyPublicBigIntId(id)}/note`,
    {
      method: "PATCH",
      body: { note },
    },
  );
  return normalizeStocktake(result);
}

/** Always re-resolve the exact current Shopify targets immediately before apply. */
export async function getStocktakeApplicationPreview(
  id: StockyPublicBigIntId,
): Promise<StocktakeApplicationPreview> {
  const canonicalId = canonicalStockyPublicBigIntId(id);
  const result = normalizeStocktakeApplicationPreview(
    await apiFetch<unknown>(`/api/stocktakes/${canonicalId}/apply-preview`, {
      cache: "no-store",
    }),
  );
  if (result.stocktake_id !== canonicalId) {
    throw new TypeError("stocktake application preview identity is invalid");
  }
  return result;
}

export async function applyStocktake(
  id: StockyPublicBigIntId,
  idempotencyKey: string,
  currentProductConfirmationToken: string | null = null,
): Promise<Stocktake> {
  const result = await apiFetch<unknown>(
    `/api/stocktakes/${canonicalStockyPublicBigIntId(id)}/apply`,
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      ...(currentProductConfirmationToken === null
        ? {}
        : {
            body: {
              current_product_confirmation_token:
                currentProductConfirmationToken,
            },
          }),
    },
  );
  return normalizeStocktake(result);
}

export async function cancelStocktake(
  id: StockyPublicBigIntId,
): Promise<Stocktake> {
  const result = await apiFetch<unknown>(
    `/api/stocktakes/${canonicalStockyPublicBigIntId(id)}/cancel`,
    {
      method: "POST",
    },
  );
  return normalizeStocktake(result);
}
